import { useEffect, useMemo, useState } from "react";
import { FaExternalLinkAlt, FaUpload } from "react-icons/fa";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { openProtectedFile } from "../api/openProtectedFile";

const statusOptions = [
  { value: "not_started", label: "Not started", className: "text-bg-secondary" },
  { value: "in_progress", label: "In progress", className: "text-bg-primary" },
  { value: "complete", label: "Complete", className: "text-bg-success" },
  { value: "blocked", label: "Blocked", className: "text-bg-danger" },
  { value: "not_applicable", label: "Not applicable", className: "text-bg-light text-dark border" }
];

function getStatusOption(status) {
  return statusOptions.find((option) => option.value === status) ?? statusOptions[0];
}

function formatFileSize(bytes) {
  const value = Number(bytes ?? 0);
  if (!value) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("bg-BG", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function DocumentItem({ phaseDocument, onUpload, uploadingDocumentTypeId }) {
  const documentType = phaseDocument.documentType;
  const instance = documentType.latestInstance;
  const uploadId = `en50716-upload-${documentType.id}`;
  const isUploading = uploadingDocumentTypeId === documentType.id;
  const uploadRoles = documentType.uploadRoles?.length ? documentType.uploadRoles.join(", ") : "PM";
  const isProducedByPreviousPhase = phaseDocument.direction === "input" && documentType.category === "output";
  const canUploadHere = documentType.canUpload && !isProducedByPreviousPhase;

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;
    await onUpload(documentType, file);
  };

  return (
    <div className={`border rounded p-2 ${instance ? "bg-white" : "bg-light"}`}>
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-semibold">{documentType.title}</div>
          <div className="text-muted small">
            Upload: {uploadRoles}
          </div>
        </div>

        {instance ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            title={instance.originalFileName}
            onClick={() => openProtectedFile(instance.fileUrl, instance.originalFileName)}
          >
            <FaExternalLinkAlt />
          </button>
        ) : (
          <span className="badge text-bg-secondary">Missing</span>
        )}
      </div>

      {instance && (
        <div className="small text-muted mt-2">
          {instance.originalFileName} · {formatFileSize(instance.fileSize)}
          <br />
          {formatDate(instance.uploadedAt)}
          {instance.uploadedBy?.name ? ` · ${instance.uploadedBy.name}` : ""}
        </div>
      )}

      {canUploadHere && (
        <div className="mt-2">
          <input
            id={uploadId}
            type="file"
            accept="application/pdf,.pdf"
            className="d-none"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          <label
            htmlFor={uploadId}
            className={`btn btn-sm ${instance ? "btn-outline-secondary" : "btn-outline-success"} mb-0`}
          >
            <FaUpload className="me-1" />
            {isUploading ? "Uploading..." : instance ? "Replace PDF" : "Upload PDF"}
          </label>
        </div>
      )}
    </div>
  );
}

export default function EN50716Docs() {
  const [phases, setPhases] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [uploadingDocumentTypeId, setUploadingDocumentTypeId] = useState(null);
  const [error, setError] = useState("");

  const loadLifecycle = async () => {
    try {
      const res = await fetchWithAuth("/api/en50716/lifecycle");

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.error || "Failed to load EN50716 lifecycle");
      }

      setPhases(await res.json());
      setError("");
    } catch (err) {
      setPhases([]);
      setError(err.message);
    }
  };

  useEffect(() => {
    loadLifecycle();
  }, []);

  const filteredPhases = useMemo(() => {
    const query = search.trim().toLowerCase();

    return phases.filter((phase) => {
      if (statusFilter !== "All" && phase.manualStatus !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const documentText = [...phase.inputs, ...phase.outputs]
        .map((phaseDocument) => phaseDocument.documentType.title)
        .join(" ");

      return (
        String(phase.name ?? "").toLowerCase().includes(query) ||
        String(phase.description ?? "").toLowerCase().includes(query) ||
        documentText.toLowerCase().includes(query)
      );
    });
  }, [phases, search, statusFilter]);

  const updatePhaseStatus = async (phase, manualStatus) => {
    const res = await fetchWithAuth(`/api/en50716/lifecycle-phases/${phase.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ manualStatus })
    });

    if (!res.ok) {
      const responseError = await res.json().catch(() => null);
      alert(responseError?.error || "Status update failed");
      return;
    }

    loadLifecycle();
  };

  const uploadDocument = async (documentType, file) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      alert("Only PDF files are supported");
      return;
    }

    setUploadingDocumentTypeId(documentType.id);
    setError("");

    try {
      const contentBase64 = await fileToBase64(file);
      const res = await fetchWithAuth(`/api/en50716/document-types/${documentType.id}/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type || "application/pdf",
          contentBase64
        })
      });

      if (!res.ok) {
        const responseError = await res.json().catch(() => null);
        throw new Error(responseError?.error || "Upload failed");
      }

      await loadLifecycle();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingDocumentTypeId(null);
    }
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const getRowStyle = (index) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff"
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="mb-1">EN50716</h3>
          <div className="text-muted">
            Lifecycle of the software development.
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            style={{ width: "160px" }}
            aria-label="Filter by status"
          >
            <option value="All">All statuses</option>
            {statusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>

          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
              type="text"
              className="form-control pe-5"
              placeholder="Search lifecycle..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          <span className="badge text-bg-secondary">
            Total: {filteredPhases.length}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}

      <div style={{ maxHeight: "68vh", overflowY: "auto" }}>
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ ...stickyHeaderStyle, width: "240px" }}>Phase</th>
              <th style={{ ...stickyHeaderStyle, width: "170px" }}>Status</th>
              <th style={{ ...stickyHeaderStyle, minWidth: "320px" }}>Input documents</th>
              <th style={{ ...stickyHeaderStyle, minWidth: "320px" }}>Output documents</th>
              <th style={{ ...stickyHeaderStyle, width: "150px" }}>Roles</th>
            </tr>
          </thead>

          <tbody>
            {filteredPhases.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center text-muted py-4">
                  No lifecycle phases found.
                </td>
              </tr>
            ) : filteredPhases.map((phase, index) => {
              const status = getStatusOption(phase.manualStatus);

              return (
                <tr key={phase.id} style={getRowStyle(index)}>
                  <td>
                    <div className="fw-semibold">{phase.orderNo}.{phase.name}</div>
                    {phase.description && (
                      <div className="text-muted small mt-1">{phase.description}</div>
                    )}
                  </td>
                  <td>
                    <div className={`badge ${status.className} mb-2`}>
                      {status.label}
                    </div>
                    <select
                      className="form-select form-select-sm"
                      value={phase.manualStatus}
                      onChange={(event) => updatePhaseStatus(phase, event.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2">
                      {phase.inputs.map((phaseDocument) => (
                        <DocumentItem
                          key={phaseDocument.id}
                          phaseDocument={phaseDocument}
                          onUpload={uploadDocument}
                          uploadingDocumentTypeId={uploadingDocumentTypeId}
                        />
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-2">
                      {phase.outputs.map((phaseDocument) => (
                        <DocumentItem
                          key={phaseDocument.id}
                          phaseDocument={phaseDocument}
                          onUpload={uploadDocument}
                          uploadingDocumentTypeId={uploadingDocumentTypeId}
                        />
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {(phase.responsibleRoles ?? []).map((roleCode) => (
                        <span key={roleCode} className="badge text-bg-light text-dark border">
                          {roleCode}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
